import { useEffect, useState } from 'react';
import {
  Title,
  Text,
  Card,
  SimpleGrid,
  Group,
  Stack,
  Badge,
  Loader,
  Alert,
  Button,
} from '@mantine/core';
import {
  IconCurrencyDollar,
  IconShoppingCart,
  IconClock,
  IconCalendarEvent,
  IconRefresh,
  IconAlertCircle,
  IconBrain,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { getSummary, getModels } from '../api/prediction-api';
import type { SummaryResponse, PredictionModel } from '../types/prediction-types';

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ComponentType<any>;
  color: string;
}) {
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Text size="sm" c="dimmed" fw={500}>
          {title}
        </Text>
        <div style={{ color }}>
          <Icon size={24} />
        </div>
      </Group>
      <Text size="xl" fw={700}>
        {value}
      </Text>
      {subtitle && (
        <Text size="xs" c="dimmed" mt={4}>
          {subtitle}
        </Text>
      )}
    </Card>
  );
}

function ModelCard({ model }: { model: PredictionModel }) {
  const targetLabels: Record<string, string> = {
    revenue: 'Revenue',
    orders: 'Orders',
    items: 'Items',
    preparation_time: 'Prep Time',
  };

  return (
    <Card shadow="sm" padding="md" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Text fw={600}>{targetLabels[model.target_variable] || model.target_variable}</Text>
        <Badge color={model.status === 'active' ? 'green' : 'gray'} size="sm">
          {model.status}
        </Badge>
      </Group>
      <Stack gap={4}>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">R²</Text>
          <Text size="sm" fw={500}>{(model.r_squared * 100).toFixed(1)}%</Text>
        </Group>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">Observations</Text>
          <Text size="sm" fw={500}>{model.total_observations}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">Games</Text>
          <Text size="sm" fw={500}>{model.total_games}</Text>
        </Group>
      </Stack>
    </Card>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [models, setModels] = useState<PredictionModel[]>([]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, modelsData] = await Promise.all([
        getSummary(),
        getModels({ status: 'active' }),
      ]);
      setSummary(summaryData);
      setModels(modelsData.results || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading dashboard...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error}
        <Button variant="light" size="xs" mt="sm" onClick={fetchData}>
          Retry
        </Button>
      </Alert>
    );
  }

  // Calculate totals from summary
  const revenueSummary = summary?.summary?.find((s) => s.target_variable === 'revenue');
  const ordersSummary = summary?.summary?.find((s) => s.target_variable === 'orders');
  const prepTimeSummary = summary?.summary?.find((s) => s.target_variable === 'preparation_time');

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={2}>Model Summary</Title>
          <Text c="dimmed" size="sm">
            Overview of active models and prediction statistics
          </Text>
        </div>
        <Button
          leftSection={<IconRefresh size={16} />}
          variant="light"
          onClick={fetchData}
        >
          Refresh
        </Button>
      </Group>

      {/* Prediction Stats */}
      <div>
        <Text fw={600} mb="sm">
          Current Predictions
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
          <StatCard
            title="Predicted Revenue"
            value={`$${(revenueSummary?.total_predicted || 0).toFixed(0)}`}
            subtitle={`${revenueSummary?.game_count || 0} games`}
            icon={IconCurrencyDollar}
            color="#40c057"
          />
          <StatCard
            title="Predicted Orders"
            value={Math.round(ordersSummary?.total_predicted || 0)}
            subtitle={`Avg ${(ordersSummary?.avg_predicted || 0).toFixed(1)} per slot`}
            icon={IconShoppingCart}
            color="#228be6"
          />
          <StatCard
            title="Prep Time"
            value={`${Math.round(prepTimeSummary?.total_predicted || 0)} min`}
            subtitle="Total predicted"
            icon={IconClock}
            color="#fab005"
          />
          <StatCard
            title="Games"
            value={revenueSummary?.game_count || 0}
            subtitle={`${revenueSummary?.slot_count || 0} time slots`}
            icon={IconCalendarEvent}
            color="#7950f2"
          />
        </SimpleGrid>
      </div>

      {/* Active Models */}
      <div>
        <Group justify="space-between" mb="sm">
          <Text fw={600}>Active Models</Text>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => navigate('/models')}
          >
            View Details →
          </Button>
        </Group>
        {models.length === 0 ? (
          <Alert color="yellow" title="No Active Models">
            No trained models found. Go to Models page to train models.
          </Alert>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            {models.map((model) => (
              <ModelCard key={model.id} model={model} />
            ))}
          </SimpleGrid>
        )}
      </div>

      {/* Quick Actions */}
      <div>
        <Text fw={600} mb="sm">
          Quick Actions
        </Text>
        <Group>
          <Button
            variant="light"
            leftSection={<IconCalendarEvent size={16} />}
            onClick={() => navigate('/predictions')}
          >
            View Predictions
          </Button>
          <Button
            variant="light"
            leftSection={<IconBrain size={16} />}
            onClick={() => navigate('/models')}
          >
            Manage Models
          </Button>
        </Group>
      </div>
    </Stack>
  );
}
